/**
 * Mobile bottom navigation bar — C2 design.
 *
 * Shows below 768px breakpoint with 5 zones:
 *   [ Score ]  [ Archive ]  [ Upload FAB ]  [ My ]  [ Games ]
 *
 * Score tab only renders when the `show_score_page` feature flag is enabled.
 * The Upload FAB floats above the bar using negative top margin.
 */
import { Link, useLocation } from 'react-router-dom';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { ScoreIcon, UploadIcon, ArchiveIcon, GamesIcon, MyScorecardIcon } from './NavIcons';
import { FeatureGate } from '@/components/FeatureGate';
import { getActiveSection } from './PressboxNav';

interface MobileNavProps {
  onUpload?: () => void;
}

export function MobileNav({ onUpload }: MobileNavProps) {
  const { isSignedIn } = useAuth();
  const { openSignIn } = useClerk();
  const { pathname } = useLocation();

  const activeSection = getActiveSection(pathname);

  const tabClass = (active: boolean) =>
    `flex-1 flex flex-col items-center gap-[3px] py-2 px-1 transition-colors ${
      active
        ? 'text-ink font-bold border-t-[2.5px] border-ink'
        : 'text-warm-gray font-medium border-t-[2.5px] border-transparent'
    } focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2`;

  const ariaCurrent = (path: string) => (activeSection === path ? ('page' as const) : undefined);

  return (
    <nav
      aria-label="Mobile navigation"
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-sand flex items-end justify-around"
      style={{ paddingBottom: 'max(10px, env(safe-area-inset-bottom))' }}
    >
      {/* Score tab — only rendered when show_score_page flag is enabled */}
      <FeatureGate flag="show_score_page">
        <Link
          to="/score"
          className={tabClass(activeSection === '/score')}
          aria-current={ariaCurrent('/score')}
        >
          <ScoreIcon size={18} />
          <span className="font-sans text-[9px] uppercase tracking-[0.3px]">Score</span>
        </Link>
      </FeatureGate>

      {/* Archive tab */}
      <Link
        to="/scorecards"
        className={tabClass(activeSection === '/scorecards')}
        aria-current={ariaCurrent('/scorecards')}
      >
        <ArchiveIcon size={18} />
        <span className="font-sans text-[9px] uppercase tracking-[0.3px]">Archive</span>
      </Link>

      {/* Upload FAB — floats above the bar */}
      <div className="flex-1 flex items-center justify-center">
        <button
          aria-label="Upload scorecard"
          onClick={isSignedIn ? onUpload : () => openSignIn()}
          className="-mt-4 w-12 h-12 rounded-full bg-gold border-2 border-white flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.15)] active:scale-95 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2"
        >
          <UploadIcon size={20} className="text-ink" />
        </button>
      </div>

      {/* My tab */}
      <Link
        to="/scorecards/my"
        className={tabClass(activeSection === '/scorecards/my')}
        aria-current={ariaCurrent('/scorecards/my')}
      >
        <MyScorecardIcon size={18} />
        <span className="font-sans text-[9px] uppercase tracking-[0.3px]">My</span>
      </Link>

      {/* Games tab */}
      <Link
        to="/gameday"
        className={tabClass(activeSection === '/gameday')}
        aria-current={ariaCurrent('/gameday')}
      >
        <GamesIcon size={18} />
        <span className="font-sans text-[9px] uppercase tracking-[0.3px]">Games</span>
      </Link>
    </nav>
  );
}

export default MobileNav;
